import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, type Location } from "react-router-dom";
import { api, errorMessage } from "../api";
import type { Comment, Item, ItemDetail, Priority, Status } from "../api/types";
import { Avatar, ErrorBox, PriorityMarker, Spinner, SyncBadges } from "../components/bits";
import { Icon } from "../components/icons";
import { DeleteItemDialog } from "../components/DeleteItemDialog";
import { QuickAdd } from "../components/QuickAdd";
import { MarkdownEditor } from "../editor/MarkdownEditor";
import { connectorLabel, formatDateTime, isoToParts, partsToIso, PRIORITY_LABELS } from "../lib/format";
import {
  createItem,
  deleteItem,
  loadCollection,
  patchItem,
  toastError,
  upsertItems,
} from "../store/app";
import {
  byPosition,
  useActorMap,
  useActors,
  useAllCollections,
  useApp,
  useBuckets,
  useItem,
  useMyActorId,
} from "../store/hooks";
import { sortedStatuses } from "./CollectionPage";
import { toggleDone } from "./ListView";

// ---------------------------------------------------------------------------

function DateField({
  label,
  iso,
  hasTime,
  onChange,
}: {
  label: string;
  iso: string | null;
  hasTime: boolean;
  onChange: (iso: string | null, hasTime: boolean) => void;
}) {
  const parts = iso ? isoToParts(iso, hasTime) : { date: "", time: null };
  const id = useMemo(() => `df-${Math.random().toString(36).slice(2)}`, []);
  return (
    <div className="date-field">
      <label htmlFor={id}>{label}</label>
      <div className="date-inputs">
        <input
          id={id}
          type="date"
          value={parts.date}
          onChange={(e) => {
            const d = e.target.value;
            if (!d) onChange(null, false);
            else onChange(partsToIso(d, parts.time), parts.time !== null);
          }}
        />
        <input
          type="time"
          aria-label={`${label} time (optional)`}
          value={parts.time ?? ""}
          disabled={!parts.date}
          onChange={(e) => {
            const t = e.target.value || null;
            onChange(partsToIso(parts.date, t), t !== null);
          }}
        />
        {iso && (
          <button type="button" className="icon-btn" aria-label={`Clear ${label}`} onClick={() => onChange(null, false)}>
            ×
          </button>
        )}
      </div>
    </div>
  );
}

function LabelsField({ labels, suggestions, onChange }: { labels: string[]; suggestions: string[]; onChange: (l: string[]) => void }) {
  const [value, setValue] = useState("");
  const listId = useMemo(() => `labels-${Math.random().toString(36).slice(2)}`, []);
  const add = () => {
    const parts = value
      .split(/[,\s]+/)
      .map((x) => x.replace(/^#/, "").trim())
      .filter(Boolean);
    if (parts.length) onChange([...new Set([...labels, ...parts])]);
    setValue("");
  };
  return (
    <div className="chips-field">
      {labels.map((l) => (
        <span key={l} className="chip chip-label removable">
          #{l}
          <button type="button" aria-label={`Remove label ${l}`} onClick={() => onChange(labels.filter((x) => x !== l))}>
            ×
          </button>
        </span>
      ))}
      <input
        aria-label="Add label"
        placeholder="Add label…"
        list={listId}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !value && labels.length) {
            onChange(labels.slice(0, -1));
          }
        }}
        onBlur={() => value && add()}
      />
      <datalist id={listId}>
        {suggestions
          .filter((s) => !labels.includes(s))
          .map((s) => (
            <option key={s} value={s} />
          ))}
      </datalist>
    </div>
  );
}

function AssigneesField({ ids, onChange }: { ids: string[]; onChange: (ids: string[]) => void }) {
  const actors = useActors();
  const map = useActorMap();
  return (
    <div className="chips-field">
      {ids.map((id) => (
        <span key={id} className="chip chip-person removable">
          <Avatar actor={map.get(id)} size={18} />
          {map.get(id)?.display_name ?? "Unknown"}
          <button type="button" aria-label={`Remove ${map.get(id)?.display_name ?? "assignee"}`} onClick={() => onChange(ids.filter((x) => x !== id))}>
            ×
          </button>
        </span>
      ))}
      <select
        aria-label="Add assignee"
        value=""
        onChange={(e) => e.target.value && onChange([...ids, e.target.value])}
      >
        <option value="">+ Assign…</option>
        {actors
          .filter((a) => !ids.includes(a.id))
          .map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name}
              {a.kind === "ghost" ? " (ghost)" : ""}
            </option>
          ))}
      </select>
    </div>
  );
}

function Comments({ itemId }: { itemId: string }) {
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const actors = useActorMap();
  useEffect(() => {
    let cancelled = false;
    api
      .comments(itemId)
      .then((r) => !cancelled && setComments(r.comments))
      .catch(() => !cancelled && setComments([]));
    return () => {
      cancelled = true;
    };
  }, [itemId]);
  return (
    <section className="panel-section">
      <h3>Comments</h3>
      {comments === null ? (
        <Spinner />
      ) : (
        <ul className="comments">
          {comments.map((c) => (
            <li key={c.id} className="comment">
              <Avatar actor={actors.get(c.author_id)} size={24} />
              <div>
                <div className="comment-head">
                  <strong>{actors.get(c.author_id)?.display_name ?? "Unknown"}</strong>
                  <span className="muted small">{formatDateTime(c.created_at)}</span>
                  {c.kind && c.kind !== "comment" && <span className="chip">{c.kind}</span>}
                </div>
                <p className="comment-body">{c.body}</p>
              </div>
            </li>
          ))}
          {comments.length === 0 && <li className="muted small">No comments yet.</li>}
        </ul>
      )}
      <form
        className="comment-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!text.trim()) return;
          setBusy(true);
          try {
            const c = await api.addComment(itemId, text.trim());
            setComments((cs) => [...(cs ?? []), c]);
            setText("");
          } catch (err) {
            toastError(err, "Could not add comment");
          } finally {
            setBusy(false);
          }
        }}
      >
        <textarea
          aria-label="New comment"
          placeholder="Write a comment…"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) (e.currentTarget.form as HTMLFormElement).requestSubmit();
          }}
        />
        <button type="submit" className="btn small" disabled={busy || !text.trim()}>
          Comment
        </button>
      </form>
    </section>
  );
}

function RelatedLink({ item }: { item: Item }) {
  const location = useLocation();
  const st = location.state as { background?: unknown; modalDepth?: number } | null;
  const bg = st?.background;
  return (
    <Link
      to={`/items/${item.id}`}
      state={bg ? { background: bg, modalDepth: (st?.modalDepth ?? 1) + 1 } : undefined}
      className="rel-link"
    >
      <PriorityMarker priority={item.priority} />
      <span className={item.status_category === "done" ? "strike" : ""}>{item.title || "(untitled)"}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------

export function ItemDetailView({
  id,
  onClose,
  variant = "page",
}: {
  id: string;
  onClose?: () => void;
  variant?: "modal" | "page";
}) {
  const [detail, setDetail] = useState<ItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const storeItem = useItem(id);
  const item = storeItem ?? detail?.item;
  const collectionDetail = useApp((s) => (item ? s.details[item.collection_id] : undefined));
  const buckets = useBuckets(item?.collection_id, !!collectionDetail?.collection.buckets_enabled);
  const allItems = useApp((s) => s.items);
  const collections = useAllCollections();
  const actorList = useActors();
  const myActorId = useMyActorId();
  const me = useApp((s) => s.me);
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bodyRefresh, setBodyRefresh] = useState(0);
  const lastVersion = useRef<number | null>(null);
  const headRef = useRef<HTMLElement>(null);
  const ready = !!(storeItem ?? detail?.item) && !!detail;

  // Sticky editor toolbar sits below the sticky modal header: expose the header height.
  useEffect(() => {
    const head = headRef.current;
    const dialog = head?.closest("dialog");
    if (!head || !dialog || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => dialog.style.setProperty("--modal-head-h", `${head.offsetHeight}px`));
    ro.observe(head);
    return () => ro.disconnect();
  }, [ready]);

  const fetchDetail = () => {
    api
      .item(id)
      .then((d) => {
        setDetail(d);
        setError(null);
        lastVersion.current = d.item.version;
        upsertItems([d.item, ...d.children]);
      })
      .catch((e) => setError(errorMessage(e)));
  };

  useEffect(() => {
    setDetail(null);
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Refetch relations etc. when the item changes remotely.
  useEffect(() => {
    if (storeItem && lastVersion.current !== null && storeItem.version > lastVersion.current) {
      lastVersion.current = storeItem.version;
      const t = window.setTimeout(fetchDetail, 600);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeItem?.version]);

  useEffect(() => {
    if (item && !collectionDetail) void loadCollection(item.collection_id).catch(() => undefined);
  }, [item, collectionDetail]);

  useEffect(() => {
    if (item) setTitle(item.title);
  }, [item?.title]); // eslint-disable-line react-hooks/exhaustive-deps

  const statuses: Status[] = useMemo(() => sortedStatuses(collectionDetail), [collectionDetail]);
  const children = useMemo(
    () => Object.values(allItems).filter((i) => i.parent_id === id).sort(byPosition),
    [allItems, id],
  );
  // Subtasks may have (anchored) lines in this item's body, e.g. nested lines from a meeting note:
  // when a child changes, refresh the detail and let a non-live editor reload.
  const childSig = children.map((c) => `${c.id}:${c.version}`).join(",");
  const firstChildSig = useRef<string | null>(null);
  useEffect(() => {
    if (!detail) return;
    if (firstChildSig.current === null) {
      firstChildSig.current = childSig;
      return;
    }
    if (firstChildSig.current === childSig) return;
    firstChildSig.current = childSig;
    const t = window.setTimeout(() => {
      fetchDetail();
      setBodyRefresh((n) => n + 1);
    }, 800);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childSig, !!detail]);

  const labelSuggestions = useMemo(() => {
    if (!item) return [];
    return [
      ...new Set(
        Object.values(allItems)
          .filter((i) => i.collection_id === item.collection_id)
          .flatMap((i) => i.labels),
      ),
    ].sort();
  }, [allItems, item]);

  if (error && !item) return <div className="item-detail-pad"><ErrorBox error={error} onRetry={fetchDetail} /></div>;
  if (!item || !detail) return <Spinner />;

  const patch = (body: Parameters<typeof patchItem>[1], optimistic?: Partial<Item>) =>
    patchItem(item.id, body, optimistic).catch((e) => toastError(e, "Update failed"));

  const collectionName = collections.find((c) => c.id === item.collection_id)?.name;
  const relations = detail.relations;

  const statusSelect = (
    <select
      className="status-select"
      aria-label="Status"
      value={item.status_id}
      onChange={(e) => {
        const s = statuses.find((x) => x.id === e.target.value);
        void patch({ status_id: e.target.value }, s ? { status_category: s.category } : {});
      }}
    >
      {statuses.length === 0 && <option value={item.status_id}>{item.status_category}</option>}
      {statuses.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );

  const deleteButton = (
    <button
      type="button"
      className="icon-btn danger"
      aria-label="Delete item"
      title="Delete item"
      onClick={async () => {
        if (item.type !== "task") {
          setConfirmDelete(true);
          return;
        }
        if (!confirm(`Delete "${item.title}"?`)) return;
        try {
          await deleteItem(item.id);
          if (onClose) onClose();
          else navigate(`/c/${item.collection_id}`);
        } catch (e) {
          toastError(e, "Delete failed");
        }
      }}
    >
      <Icon name="trash" />
    </button>
  );

  const afterDelete = () => {
    if (item.type === "meeting") navigate("/meetings", { replace: variant === "modal", state: null });
    else if (onClose) onClose();
    else navigate(`/c/${item.collection_id}`);
  };

  return (
    <article className={`item-detail is-${variant}`} aria-labelledby={`item-title-${item.id}`}>
      <header className="item-header" ref={headRef}>
        <div className="item-header-main">
          <input
            type="checkbox"
            className="row-check big"
            aria-label={item.status_category === "done" ? "Reopen" : "Complete"}
            checked={item.status_category === "done"}
            onChange={() => toggleDone(item, statuses)}
          />
          <textarea
            id={`item-title-${item.id}`}
            className="item-title-input"
            aria-label="Title"
            rows={1}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            onBlur={() => {
              const t = title.trim();
              if (t && t !== item.title) void patch({ title: t });
              else setTitle(item.title);
            }}
          />
          <div className="item-header-actions">
            {deleteButton}
            {variant === "modal" && (
              <Link
                className="icon-btn"
                to={`/items/${item.id}`}
                state={null}
                aria-label="Open full page"
                title="Open full page"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/items/${item.id}`, { replace: true, state: null });
                }}
              >
                <Icon name="expand" />
              </Link>
            )}
            {onClose && (
              <button type="button" className="icon-btn" aria-label="Close" title="Close (Esc)" onClick={onClose}>
                <Icon name="close" />
              </button>
            )}
          </div>
        </div>
        <div className="item-header-meta">
          {statusSelect}
          <SyncBadges sync={item.sync} />
          <span className="crumbs muted small">
            {collectionName && <Link to={`/c/${item.collection_id}`}>{collectionName}</Link>}
            <span className="chip">{item.type}</span>
            {item.type === "meeting" && <Link to={`/meetings/${item.id}`}>Open meeting view</Link>}
          </span>
        </div>
      </header>

      <div className="item-columns">
        <div className="item-main">
          <section className="panel-section first">
            <h3 className="sr-only">Description</h3>
            <MarkdownEditor
              key={item.body_doc_id ?? "detached"}
              docId={item.body_doc_id}
              initialMarkdown={detail.body}
              externalVersion={bodyRefresh}
              onDetachedSave={async (md) => {
                const updated = await api.patchItem(item.id, { body: md });
                upsertItems([updated]);
              }}
              ariaLabel="Item description"
              user={me ? { name: actorList.find((a) => a.id === myActorId)?.display_name ?? me.account.email } : undefined}
              context={{
                members: actorList,
                labels: labelSuggestions,
                collections: collections.filter((c) => c.space_id === item.space_id).map((c) => c.name),
              }}
            />
          </section>

          <section className="panel-section">
            <h3>
              Subtasks <span className="count">{children.length}</span>
            </h3>
            <ul className="rows compact">
              {children.map((c) => (
                <li key={c.id} className={`row${c.status_category === "done" ? " done" : ""}`}>
                  <input
                    type="checkbox"
                    className="row-check"
                    checked={c.status_category === "done"}
                    aria-label={`Toggle ${c.title}`}
                    onChange={() => toggleDone(c, statuses)}
                  />
                  <RelatedLink item={c} />
                  <span className="row-meta">
                    <SyncBadges sync={c.sync} />
                  </span>
                </li>
              ))}
            </ul>
            <QuickAdd
              compact
              label="Add subtask"
              placeholder="Add subtask…"
              actors={actorList}
              statuses={statuses}
              onCreate={async (body) => {
                try {
                  await createItem(item.collection_id, { ...body, parent_id: item.id });
                } catch (e) {
                  toastError(e, "Could not add subtask");
                  throw e;
                }
              }}
            />
          </section>

          {relations.length > 0 && (
            <section className="panel-section">
              <h3>Relations</h3>
              <ul className="relations">
                {relations.map((r) => (
                  <li key={`${r.type}-${r.direction}-${r.item.id}`}>
                    <span className="chip">
                      {r.type.replace(/_/g, " ")} {r.direction === "out" ? "→" : "←"}
                    </span>
                    <RelatedLink item={allItems[r.item.id] ?? r.item} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <Comments itemId={item.id} />
        </div>
        {confirmDelete && (
          <DeleteItemDialog item={item} onClose={() => setConfirmDelete(false)} onDeleted={afterDelete} />
        )}

        <aside className="item-side" aria-label="Properties">
          <div className="props">
            <label className="prop">
              <span>Priority</span>
              <select
                value={item.priority}
                onChange={(e) => void patch({ priority: Number(e.target.value) as Priority })}
              >
                {([0, 1, 2, 3, 4] as Priority[]).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </label>
            {collectionDetail?.collection.buckets_enabled && (
              <label className="prop">
                <span>Bucket</span>
                <select
                  value={item.bucket_id ?? ""}
                  onChange={(e) => void patch({ bucket_id: e.target.value || null })}
                >
                  <option value="">No bucket</option>
                  {buckets.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                  {item.bucket_id && buckets.length > 0 && !buckets.some((b) => b.id === item.bucket_id) && (
                    <option value={item.bucket_id}>(unknown bucket)</option>
                  )}
                </select>
              </label>
            )}
            <div className="prop">
              <DateField
                label="Start"
                iso={item.start_at}
                hasTime={item.start_at ? !item.start_at.endsWith("T00:00:00Z") : false}
                onChange={(iso) => void patch({ start_at: iso })}
              />
            </div>
            <div className="prop">
              <DateField
                label="Due"
                iso={item.due_at}
                hasTime={item.due_has_time}
                onChange={(iso, hasTime) => void patch({ due_at: iso, due_has_time: hasTime })}
              />
            </div>
            <div className="prop">
              <span className="prop-label">Labels</span>
              <LabelsField labels={item.labels} suggestions={labelSuggestions} onChange={(labels) => void patch({ labels })} />
            </div>
            <div className="prop">
              <span className="prop-label">Assignees</span>
              <AssigneesField ids={item.assignees} onChange={(assignees) => void patch({ assignees })} />
            </div>
          </div>

          <section className="panel-section">
            <h3>Sync</h3>
            {item.sync.length === 0 ? (
              <p className="muted small">Not linked to any external tool.</p>
            ) : (
              <ul className="sync-list">
                {item.sync.map((s) => (
                  <li key={s.binding_id}>
                    <span className={`sync-badge ${s.state}`}>
                      <span className="dot" aria-hidden="true" />
                      {connectorLabel(s.connector_type)} · {s.state.replace("_", " ")}
                    </span>
                    {s.remote_url ? (
                      <a href={s.remote_url} target="_blank" rel="noreferrer noopener" className="small">
                        {s.remote_id ?? "open"}
                      </a>
                    ) : (
                      <span className="muted small">{s.remote_id ?? "—"}</span>
                    )}
                    {s.acted_as && (
                      <span className="muted small">
                        {s.acted_as === "user" ? "pushed as you" : "via shared connection"}
                      </span>
                    )}
                    {s.last_error && <div className="form-error small">{s.last_error}</div>}
                  </li>
                ))}
              </ul>
            )}
            <p className="muted small">
              Created {formatDateTime(item.created_at)} · updated {formatDateTime(item.updated_at)} · v{item.version}
            </p>
          </section>
        </aside>
      </div>
    </article>
  );
}

interface ModalState {
  background?: Location;
  modalDepth?: number;
}

/** Centered floating dialog rendered over the background route (collection, my tasks, …). */
export function ItemModal() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const depth = (location.state as ModalState | null)?.modalDepth ?? 1;
  const depthRef = useRef(depth);
  depthRef.current = depth;
  const close = () => navigate(-depthRef.current);

  useEffect(() => {
    const d = ref.current;
    opener.current = document.activeElement;
    if (d && !d.open) d.showModal();
    return () => {
      d?.close();
      const o = opener.current as HTMLElement | null;
      if (o && o.isConnected && typeof o.focus === "function") o.focus({ preventScroll: true });
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className="item-modal"
      aria-label="Item details"
      onCancel={(e) => {
        e.preventDefault();
        // First Esc leaves the editor / an input, the second closes the dialog.
        const active = document.activeElement as HTMLElement | null;
        if (active && active.closest(".cm-editor")) {
          ref.current?.focus();
          return;
        }
        close();
      }}
      onMouseDown={(e) => {
        if (e.target === ref.current) close(); // backdrop click
      }}
      tabIndex={-1}
    >
      <ItemDetailView id={id} onClose={close} variant="modal" />
    </dialog>
  );
}

/** Full-page item view (direct link / reload). */
export function ItemPage() {
  const { id = "" } = useParams();
  return (
    <div className="page item-page">
      <ItemDetailView id={id} variant="page" />
    </div>
  );
}
